// src/components/admin/UpdateProductModal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Package } from 'lucide-react';
import { Modal, Button } from '../common';
import { Category, SubcategoryHierarchy, Product } from '../../types';
import { productService } from '@/services/api';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface UpdateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  categories: Category[];
  allSubcategories: Array<
    SubcategoryHierarchy & { categoryName: string; categoryId: string; level?: number }
  >;
  onUpdated: () => void; // ✅ ahora no requiere Promise
  isSubmitting: boolean;
}

export const UpdateProductModal: React.FC<UpdateProductModalProps> = ({
  isOpen,
  onClose,
  product,
  categories,
  allSubcategories,
  onUpdated,
  isSubmitting
}) => {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      name: '',
      description: '',
      internalCode: '',
      subcategoryId: '',
      imageUrl: '',
      imageFile: undefined as FileList | undefined,
    },
  });

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);

  // Prefill al abrir
  useEffect(() => {
    if (product) {
      reset({
        name: product.name,
        description: product.description || '',
        internalCode: product.internalCode || '',
        subcategoryId: (product as any).subcategoryId || '',
        imageUrl: product.imageUrl || '',
      });

      setOriginalImageUrl(product.imageUrl || null);

      const sub = allSubcategories.find(s => s.id === (product as any).subcategoryId);
      if (sub) setSelectedCategoryId(sub.categoryId);

      if (product.imageUrl) {
        const imgSrc = product.imageUrl.startsWith('http')
          ? product.imageUrl
          : `${API_BASE}${product.imageUrl}`;
        setPreviewUrl(imgSrc);
      }
    }
  }, [product, reset, allSubcategories]);

  // Preview de archivo
  const imageFileList = watch('imageFile');
  const selectedFile = imageFileList && imageFileList.length > 0 ? imageFileList[0] : null;

  useEffect(() => {
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert('El archivo supera el límite de 5 MB.');
        setPreviewUrl(null);
        return;
      }
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (product?.imageUrl) {
      const imgSrc = product.imageUrl.startsWith('http')
        ? product.imageUrl
        : `${API_BASE}${product.imageUrl}`;
      setPreviewUrl(imgSrc);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile, product]);

  // Filtrar subcategorías por categoría
  const availableSubcategories = useMemo(() => {
    if (!selectedCategoryId) return [];
    return allSubcategories.filter(sub => sub.categoryId === selectedCategoryId);
  }, [selectedCategoryId, allSubcategories]);

  const handleCategoryChange = (id: string) => {
    setSelectedCategoryId(id);
    setValue('subcategoryId', '');
  };

  // Envío
  const onSubmitForm = async (data: any) => {
    if (!product) return;
    try {
      const file = data.imageFile?.[0];
      let finalImageUrl: string | null = originalImageUrl;

      if (file) {
        finalImageUrl = null;
      } else if (data.imageUrl && /^https?:\/\//i.test(data.imageUrl.trim())) {
        finalImageUrl = data.imageUrl.trim();
      } else if (data.imageUrl === '') {
        finalImageUrl = originalImageUrl;
      }

      const submitData = {
        name: data.name,
        description: data.description,
        internalCode: data.internalCode,
        subcategoryId: data.subcategoryId,
        imageFile: file ?? undefined,
        imageUrl: finalImageUrl,
      };

      await productService.update(product.id, submitData);
      onUpdated(); // ✅ ya no espera Promise
      reset();
      setSelectedCategoryId('');
      onClose();
    } catch (err) {
      console.error('[UpdateProductModal] Error al actualizar producto:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar Producto" size="lg">
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">Nombre *</label>
          <input
            type="text"
            {...register('name', { required: 'El nombre es requerido' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
          />
          {errors.name && <p className="text-sm text-red-600">{errors.name.message as string}</p>}
        </div>

        {/* Código interno */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">Código Interno</label>
          <input
            type="text"
            {...register('internalCode')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
          />
        </div>

        {/* Subcategoría */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">Subcategoría *</label>
          <select
            {...register('subcategoryId', { required: 'Requerido' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
          >
            <option value="">Seleccionar...</option>
            {availableSubcategories.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.size})</option>
            ))}
          </select>
        </div>

        {/* Imagen */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              Imagen (archivo)
            </label>
            <input type="file" accept="image/*" {...register('imageFile')} />
            {previewUrl && (
              <div className="mt-3">
                <img
                  src={previewUrl}
                  className="max-h-40 object-contain mx-auto border p-2 rounded-lg"
                  alt="Vista previa"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">URL de imagen</label>
            <input
              type="text"
              {...register('imageUrl')}
              disabled={!!selectedFile}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
            />
          </div>
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">Descripción</label>
          <textarea
            {...register('description')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" isLoading={isSubmitting} leftIcon={<Package className="w-4 h-4" />}>
            Guardar Cambios
          </Button>
        </div>
      </form>
    </Modal>
  );
};
